import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../store';
import { ErrorType, IAddress } from '../../../domains/common.domain';
import { usePlacesWidget } from 'react-google-autocomplete';
import { AddressFormat } from '../../../domains/tenant-config';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { AddressCepFormat } from './address-cep-format';
import { AddressStreetFormat } from './address-street-format';
import { AddressCountriesFormat } from './address-countries-format';
import { AddressDefaultFormat } from './address-default-format';
import StringInput from '../inputs/string-input';
import SelectInput, { ISelectValue } from '../inputs/select-input';
import useRegisterErrorField from '../hooks/register-error-field-hook';
import { ICountry } from '../../../domains/tenant.domain';
import { getLanguageCode } from '../../../translations/i18n';
import { replaceSpecialChars } from '../../../utils/special-chars';

export interface IAddressWrapperProps {
  address: IAddress;
  handleChange: (field: keyof IAddress, value: string) => void;
  setError: (field: keyof IAddress, errorType: ErrorType) => void;
  selectedCountry: ICountry;
  showRecipient?: boolean;
  onCityNormalized: (city: string) => void;
}

export interface IAddressProps {
  addressInputs: IAddressInputs;
}

export interface IAddressInputs {
  countryInput: React.JSX.Element;
  zipCodeInput: React.JSX.Element;
  cityInput: React.JSX.Element;
  secondAddressInput: React.JSX.Element;
  addressInput: React.JSX.Element;
  stateDropDownInput: React.JSX.Element;
  stateTextInput: React.JSX.Element;
  streetNumberInput: React.JSX.Element;
  apartmentInput: React.JSX.Element;
  floorInput: React.JSX.Element;
  withoutNumberCheckbox: React.JSX.Element;
  complementInput: React.JSX.Element;
  neighborhoodInput: React.JSX.Element;
  recipientInput?: React.JSX.Element | undefined;
}

export const Address = observer((props: IAddressWrapperProps) => {
  const { t } = useTranslation('', { keyPrefix: 'ADDRESS' });
  const { t: tError } = useTranslation('', { keyPrefix: 'ADDRESS.ERRORS' });
  const { address, handleChange, setError, selectedCountry, showRecipient, onCityNormalized } = props;
  const { tenantStore, signUpStore } = useStore();
  const { useAddressAutocomplete, addressFormat, displayCityDropdown } = tenantStore.config;
  const secondaryAddressRef = useRef<HTMLInputElement>(null);
  const options = {
    componentRestrictions: { country: tenantStore.countries.map(x => x.code) },
    types: ['address'],
  };

  const addressRef = useRef<HTMLDivElement | null>(null);
  useRegisterErrorField(addressRef, address.address.errorType);

  const { ref: addressAutocompleteRef } = useAddressAutocomplete
    ? usePlacesWidget<HTMLInputElement>({
        apiKey: tenantStore.config.autocompleteApiKey,
        onPlaceSelected: place => fillAddress(place),
        options,
        language: getLanguageCode(),
      })
    : { ref: useRef<HTMLInputElement>(null) };

  const fillAddress = useCallback(
    (place: any) => {
      if (!place?.address_components) return;

      let address1 = '';
      let address1Short = '';
      let city = '';
      let state = '';
      let postcode = '';
      let country = '';
      let subLocality = '';
      const selectedAddressString = address.address.value;
      const selectedAddressArray = selectedAddressString ? selectedAddressString.split(',') : null;
      const selectedAddress1 = selectedAddressArray ? selectedAddressArray[0] : null;

      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'street_number': {
            address1 = `${component.long_name} ${address1}`;
            address1Short = `${component.short_name} ${address1Short}`;
            break;
          }
          case 'route': {
            address1 += component.long_name;
            address1Short += component.short_name;
            break;
          }
          case 'postal_code': {
            postcode = `${component.long_name}${postcode}`;
            break;
          }
          case 'locality': {
            city = component.long_name;
            break;
          }
          case 'administrative_area_level_1': {
            state = component.short_name;
            break;
          }
          case 'sublocality_level_1': {
            subLocality = component.short_name;
            break;
          }
          case 'country': {
            country = component.short_name;
            break;
          }
        }
      }

      handleChange('country', country);
      handleChange('state', state);
      handleChange('address', address1);
      handleChange('city', city || subLocality);
      handleChange('zipCode', postcode);
      handleChange('autocompleteInputAddress', address1);
      handleChange('autocompleteShortAddress', address1Short);
      handleChange('autocompleteSelectedAddress', selectedAddress1 ?? '');
      secondaryAddressRef?.current?.focus();
    },
    [address, handleChange, secondaryAddressRef],
  );

  const onClear = useCallback(() => {
    handleChange('country', '');
    handleChange('state', '');
    handleChange('address', '');
    handleChange('city', '');
    handleChange('zipCode', '');
    handleChange('autocompleteInputAddress', '');
    handleChange('autocompleteShortAddress', '');
    handleChange('autocompleteSelectedAddress', '');
  }, [address, handleChange]);

  const [states, setStates] = useState<ISelectValue[]>([]);

  useEffect(() => {
    if (address.country.value) {
      tenantStore.getRegions(address.country.value).then(regions => {
        setStates(regions.map(region => ({ label: region.localizedName ?? region.regionName, value: region.regionCode })));
      });
    }
  }, [address.country.value, tenantStore]);

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, field: keyof IAddress) => {
    const pastedText = event?.clipboardData?.getData('text/plain');
    handleChange(field, pastedText);
    event.preventDefault();
  };

  const [cities, setCities] = useState<ISelectValue[]>([]);
  const citySelectLabel = useMemo(
    () => (address.state.value ? t('SELECT_CITY') : tError('PLEASE_SELECT_STATE')),
    [address.state.value, t, tError],
  );

  useEffect(() => {
    if (!displayCityDropdown) return;

    if (!address.country.value || !address.state.value) {
      handleChange('city', '');
      setCities([]);
      return;
    }

    tenantStore.getCities(address.country.value, address.state.value).then(cities => {
      setCities(cities.map(city => ({ label: city, value: city })));
      const city = cities?.find(
        x => replaceSpecialChars(x.trim().toLowerCase()) === replaceSpecialChars(address.city.value?.trim()?.toLowerCase()),
      );
      if (city) onCityNormalized(city);
      else handleChange('city', '');
    });
  }, [address.country.value, address.state.value, tenantStore, displayCityDropdown, handleChange]);

  const inputs: IAddressInputs = {
    addressInput: (
      <div className={'container-input'} ref={addressRef}>
        <label>{t('ADDRESS')}</label>
        <div className={'controls'}>
          {useAddressAutocomplete && (
            <div className={'control-input-btn'}>
              <button className={'n2pc-btn-text clear-input-btn'} onClick={e => onClear()}>
                {t('CLEAR')}
              </button>
            </div>
          )}

          <input
            type="text"
            name={'line1'}
            ref={addressAutocompleteRef}
            className={classNames(address.address.errorType && address.address.errorType !== 'digits' && signUpStore.showErrors && 'error')}
            value={address.address.value}
            onChange={e => {
              handleChange('address', e.target.value);
              useAddressAutocomplete && setError('address', 'select_required');
            }}
          />
          {address.address.errorType &&
            (signUpStore.showErrors || address.address.errorType === 'select_required' || address.address.errorType === 'digits') && (
              <label className={address.address.errorType === 'digits' ? 'warning' : 'error'}>
                {tError(`${address.address.errorType.toUpperCase()}_ADDRESS`)}
              </label>
            )}
        </div>
      </div>
    ),
    secondAddressInput: (
      <div className={'container-input'}>
        <label>{t('ADDRESS_2')}</label>
        <div className={'controls'}>
          <input
            type="text"
            ref={secondaryAddressRef}
            name={'line2'}
            className={classNames(address.secondaryAddress.errorType && signUpStore.showErrors && 'error')}
            value={address.secondaryAddress.value ?? ''}
            onChange={e => handleChange('secondaryAddress', e.target.value)}
          />
        </div>
      </div>
    ),
    complementInput: (
      <StringInput
        value={address.secondaryAddress}
        label={t('COMPLEMENT')}
        name={'line2'}
        onUpdate={value => handleChange('secondaryAddress', value)}
      />
    ),
    withoutNumberCheckbox: (
      <label className={'n2pc-checkbox in-column'}>
        <input type="checkbox" checked={address.withoutNumber.value} onChange={e => handleChange('withoutNumber', e.target.value)} />
        <span className={'n2pc-checkbox-mask'}></span>
        <span className={'checkbox-label'}>{t('WITHOUT_NUMBER')}</span>
      </label>
    ),
    streetNumberInput: (
      <StringInput
        value={address.streetNumber}
        label={t('STREET_NUMBER')}
        name={'streetNumber'}
        onUpdate={value => handleChange('streetNumber', value)}
        error={address.streetNumber.errorType ? tError(`${address.streetNumber.errorType.toUpperCase()}_STREET_NUMBER`) : ''}
        disabled={address.withoutNumber.value}
      />
    ),
    floorInput: <StringInput value={address.floor} label={t('FLOOR')} name={'floor'} onUpdate={value => handleChange('floor', value)} />,
    apartmentInput: (
      <StringInput
        value={address.apartment}
        label={t('APARTMENT')}
        name={'apartment'}
        onUpdate={value => handleChange('apartment', value)}
      />
    ),
    neighborhoodInput: (
      <StringInput
        value={address.neighborhood}
        label={t('NEIGHBORHOOD')}
        name={'neighborhood'}
        onUpdate={value => handleChange('neighborhood', value)}
        error={address.neighborhood.errorType ? tError(`${address.neighborhood.errorType.toUpperCase()}_NEIGHBORHOOD`) : ''}
      />
    ),
    zipCodeInput: (
      <StringInput
        value={address.zipCode}
        label={t('ZIP')}
        name={'zip'}
        onUpdate={value => handleChange('zipCode', value)}
        onPaste={e => handlePaste(e, 'zipCode')}
        maxLength={selectedCountry.maxZipLength}
        error={
          address.zipCode.errorType ? tError(`${address.zipCode.errorType.toUpperCase()}_ZIP`, { data: selectedCountry.minZipLength }) : ''
        }
      />
    ),
    stateDropDownInput: (
      <SelectInput
        value={address.state}
        label={t('STATE')}
        error={address.state.errorType ? tError(`${address.state.errorType.toUpperCase()}_STATE`) : ''}
        selectLabel={t('SELECT_STATE')}
        onUpdate={value => handleChange('state', value)}
        selectValues={states}
      />
    ),
    stateTextInput: (
      <StringInput
        value={address.state}
        label={t('STATE')}
        name={'state'}
        onUpdate={value => handleChange('state', value)}
        error={address.state.errorType ? tError(`${address.state.errorType.toUpperCase()}_STATE`) : ''}
      />
    ),
    cityInput: displayCityDropdown ? (
      <SelectInput
        value={address.city}
        label={t('CITY')}
        error={address.city.errorType ? tError(`${address.city.errorType.toUpperCase()}_CITY`) : ''}
        selectLabel={citySelectLabel}
        onUpdate={value => handleChange('city', value)}
        selectValues={cities}
      />
    ) : (
      <StringInput
        value={address.city}
        label={t('CITY')}
        name={'city'}
        onUpdate={value => handleChange('city', value)}
        error={address.city.errorType ? tError(`${address.city.errorType.toUpperCase()}_CITY`) : ''}
      />
    ),
    countryInput: (
      <SelectInput
        value={address.country}
        label={t('COUNTRY')}
        error={address.country.errorType ? tError(`${address.country.errorType.toUpperCase()}_COUNTRY`) : ''}
        selectLabel={t('SELECT_COUNTRY')}
        onUpdate={value => handleChange('country', value)}
        selectValues={tenantStore.countries.map(country => ({ label: country.localizedName ?? country.name, value: country.code }))}
        disabled={tenantStore.countries.length === 1}
      />
    ),
    recipientInput: showRecipient ? (
      <StringInput
        value={address.recipient}
        label={t('RECIPIENT')}
        name={'recipient'}
        onUpdate={value => handleChange('recipient', value)}
        error={address.recipient.errorType ? tError(`${address.recipient.errorType.toUpperCase()}_RECIPIENT`) : ''}
      />
    ) : undefined,
  };

  switch (addressFormat) {
    case AddressFormat.cep:
      return <AddressCepFormat addressInputs={inputs} />;
    case AddressFormat.street:
      return <AddressStreetFormat addressInputs={inputs} />;
    case AddressFormat.countries:
      return <AddressCountriesFormat addressInputs={inputs} />;
    default:
      return <AddressDefaultFormat addressInputs={inputs} />;
  }
});
